import { Trans, useLingui } from "@lingui/react/macro";
import { Button, cn, Input, Textarea, toast } from "@rakazo/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useSyncExternalStore } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getTicketBoardEnabled, subscribeTicketBoardEnabled } from "../../lib/bootstrap";
import { useSpaceQueries } from "../../lib/query";
import { rpc } from "../../lib/rpc";
import { useBotSessions } from "../../lib/use-bot-activity";
import { errorText } from "../../lib/user-error";

/**
 * A person starts a session of the bot: the task becomes a ticket of the bot whose session starts at
 * once, and its chat opens.
 */
function NewSessionForm({ botId, onDone }: { botId: string; onDone: () => void }) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queries = useSpaceQueries();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [starting, setStarting] = useState(false);
  const start = async () => {
    const task = title.trim();
    if (!task || starting) return;
    setStarting(true);
    try {
      const { sessionId } = await rpc.bots.startSession({
        botId,
        title: task,
        description: description.trim() || undefined,
      });
      await queryClient.invalidateQueries({ queryKey: queries.bots.sessions.key() });
      onDone();
      navigate(`/app/${botId}/s/${sessionId}`);
    } catch (error) {
      toast.error(errorText(error, t`Could not start the session. Try again.`));
    } finally {
      setStarting(false);
    }
  };
  return (
    <form
      data-testid="new-session-form"
      className="flex flex-col gap-1.5 px-1.5 pb-2"
      onSubmit={(event) => {
        event.preventDefault();
        void start();
      }}
    >
      <Input
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder={t`Task in a few words`}
        maxLength={200}
        aria-label={t`Task`}
        autoFocus
      />
      <Textarea
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder={t`Details, goal, when it is done (optional)`}
        aria-label={t`Details`}
        rows={3}
      />
      <div className="flex justify-end gap-1.5">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          <Trans>Cancel</Trans>
        </Button>
        <Button type="submit" size="sm" disabled={starting || !title.trim()}>
          <Trans>Start session</Trans>
        </Button>
      </div>
    </form>
  );
}

/**
 * jhl-Bots (Sitzungen): the bot's work sessions under its to-do list. Each ticket the bot works on
 * runs in its own session; a line opens that session's chat. Closed sessions of the last week show
 * on request, and the person can start a new session for a task of their own.
 */
export function BotSessionsList({ botId }: { botId: string }) {
  const { t } = useLingui();
  const [showClosed, setShowClosed] = useState(false);
  const [starting, setStarting] = useState(false);
  const board = useSyncExternalStore(
    subscribeTicketBoardEnabled,
    getTicketBoardEnabled,
    () => false,
  );
  const sessions = useBotSessions(botId, showClosed);
  const open = sessions.filter((session) => session.status === "open");
  const closed = sessions.filter((session) => session.status === "closed");
  if (!board) return null;
  return (
    <section
      data-testid="bot-sessions"
      aria-label={t`Sessions`}
      className="mt-2 w-full rounded-xl border border-sidebar-border bg-background/95 p-2 text-sm shadow-sm"
    >
      <div className="flex items-center justify-between px-1.5 pb-1 text-xs text-muted-foreground">
        <span>
          <Trans>Sessions</Trans> · {open.length}
        </span>
        <span className="flex gap-3">
          <button type="button" className="underline" onClick={() => setShowClosed(!showClosed)}>
            {showClosed ? <Trans>Hide ended</Trans> : <Trans>Show ended</Trans>}
          </button>
          {starting ? null : (
            <button
              type="button"
              className="underline"
              data-testid="new-session"
              onClick={() => setStarting(true)}
            >
              <Trans>New session</Trans>
            </button>
          )}
        </span>
      </div>
      {starting ? <NewSessionForm botId={botId} onDone={() => setStarting(false)} /> : null}
      <ul className="flex flex-col">
        {[...open, ...closed].map((session) => (
          <li key={session.id}>
            <Link
              to={`/app/${botId}/s/${session.id}`}
              className={cn(
                "flex items-center gap-1.5 rounded-lg px-1.5 py-1 hover:bg-muted",
                session.working && "bg-status-working/10",
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  session.status === "closed"
                    ? "bg-muted-foreground/40"
                    : session.working
                      ? "bg-status-working"
                      : "bg-muted-foreground",
                )}
              />
              <span
                className={cn("truncate", session.status === "closed" && "text-muted-foreground")}
                title={session.title}
              >
                {session.title}
              </span>
              <span className="sr-only">
                , {session.status === "closed" ? t`ended` : session.working ? t`working` : t`idle`}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
